import api from '@pray/shared/utils/api';

import type {
  ApplyJob,
  Replacement,
  ReplacementMatches,
  SaveTranscriptionSettingsBody,
  TranscriptionSettings,
} from './types';

type Envelope<T> = { data: T };

const artistPath = (artistId: string) => {
  if (!artistId) throw Error('Artist ID is required');
  return `/web/studio/artists/${artistId}`;
};

export async function getTranscriptionSettings({ artistId }: { artistId: string }): Promise<TranscriptionSettings> {
  const response = await api.executeGetRequest<Envelope<TranscriptionSettings>>(
    `${artistPath(artistId)}/settings/transcription`
  );
  return response.data.data;
}

export async function saveTranscriptionSettings({
  artistId,
  settings,
}: {
  artistId: string;
  settings: SaveTranscriptionSettingsBody;
}): Promise<TranscriptionSettings> {
  const response = await api.executePutRequest<Envelope<TranscriptionSettings>>(
    `${artistPath(artistId)}/settings/transcription`,
    settings
  );
  return response.data.data;
}

export async function getReplacementMatches({ artistId }: { artistId: string }): Promise<ReplacementMatches> {
  const response = await api.executeGetRequest<Envelope<ReplacementMatches>>(
    `${artistPath(artistId)}/transcripts/replacement-matches`
  );
  return response.data.data;
}

export async function applyReplacements({ artistId }: { artistId: string }): Promise<{ job_id: number }> {
  const response = await api.executePostRequest<Envelope<{ job_id: number }>>(
    `${artistPath(artistId)}/transcripts/apply-replacements`,
    {}
  );
  return response.data.data;
}

export async function getApplyJob({ artistId, jobId }: { artistId: string; jobId: number }): Promise<ApplyJob> {
  const response = await api.executeGetRequest<Envelope<ApplyJob>>(
    `${artistPath(artistId)}/transcripts/apply-replacements/${jobId}`
  );
  return response.data.data;
}

export async function acceptCorrections({
  artistId,
  ids,
}: {
  artistId: string;
  ids: number[];
}): Promise<{ replacements: Replacement[] }> {
  const response = await api.executePostRequest<Envelope<{ replacements: Replacement[] }>>(
    `${artistPath(artistId)}/transcripts/corrections/accept`,
    { ids }
  );
  return response.data.data;
}

export async function dismissCorrections({ artistId, ids }: { artistId: string; ids: number[] }): Promise<void> {
  await api.executePostRequest(`${artistPath(artistId)}/transcripts/corrections/dismiss`, { ids });
}
